import { DocumentStatusV2Schema, PipelineStatusSchema } from '@/client/types.gen.ts';
import { PipelineStatus } from '@/constants/pipeline';

export function isProcessing(status?: string | null): boolean {
	return status === PipelineStatus.Pending || status === PipelineStatus.InProgress;
}

export function isCompleted(status?: string | null): boolean {
	return status === PipelineStatus.Completed;
}

/**
 * Only FAILED_EXHAUSTED and FAILED_PERMANENT are statuses the backend itself will never retry
 * (backend/pipelines/retry.py _HARD_TERMINAL_FAILED) — deliberately treated as the only genuine
 * failures here. The other non-terminal statuses read as still working:
 * - FAILED_STALE is unconditionally re-queued by the sweeper every pass (backend/pipelines/sweeper.py)
 *   until max_retries promotes it to FAILED_EXHAUSTED.
 * - CANCELED on a live (non-deleted) document means a content-hash mismatch mid-run, with a new run
 *   for the updated content following; the sweeper's other CANCELED case (abandoned runs on
 *   soft-deleted documents) shouldn't reach a document the UI is showing.
 * - Plain FAILED is usually retried automatically (Celery's own retry, or the sweeper's Loop A for
 *   overdue retries), but can rarely dead-end when next_retry_at is null. PipelineStatusSchema does
 *   not expose next_retry_at, so the frontend only treats it as stalled after a separate grace period.
 */
export function isPermanentlyFailed(status?: string | null): boolean {
	return status === PipelineStatus.FailedExhausted || status === PipelineStatus.FailedPermanent;
}

export function isFailed(status?: string | null): boolean {
	return isPermanentlyFailed(status);
}

export function bothPipelinesCompleted(bnd?: string | null, kb?: string | null): boolean {
	return isCompleted(bnd) && isCompleted(kb);
}

// Helpers operating on PipelineStatusSchema objects (avoid inline logic anywhere)
export function isRunProcessing(run?: PipelineStatusSchema | null): boolean {
	return isProcessing(run?.status ?? undefined);
}

export function isRunCompleted(run?: PipelineStatusSchema | null): boolean {
	return isCompleted(run?.status ?? undefined);
}

export function isRunFailed(run?: PipelineStatusSchema | null): boolean {
	return isFailed(run?.status ?? undefined);
}

export function getRunProgress(run?: PipelineStatusSchema | null): number | undefined {
	return run?.progress ?? undefined;
}

export function getRunError(run?: PipelineStatusSchema | null): string | undefined {
	return run?.error ?? undefined;
}

export function hasPermanentFailure(doc?: DocumentStatusV2Schema | null): boolean {
	if (!doc) return false;
	return [doc.bnd_v2, doc.metadata_profiling, doc.metadata_extraction, doc.kbs, doc.document_enrichment].some((run) =>
		isPermanentlyFailed(run?.status),
	);
}

export function hasKnowledgeBasePermanentFailure(doc?: DocumentStatusV2Schema | null): boolean {
	if (!doc) return false;
	return [doc.bnd_v2, doc.metadata_profiling, doc.metadata_extraction, doc.kbs].some((run) =>
		isPermanentlyFailed(run?.status),
	);
}

export function isDocumentProcessingComplete(doc?: DocumentStatusV2Schema | null): boolean {
	return isRunCompleted(doc?.kbs) && isRunCompleted(doc?.document_enrichment);
}

export const DOCUMENT_PROCESSING_DELAY_MS = 25 * 60 * 1000;
export const DOCUMENT_PROCESSING_STALLED_MS = 6 * 60 * 60 * 1000;
export const RECOVERABLE_FAILURE_STALLED_MS = 60 * 60 * 1000;

function isRecoverableTerminalStatus(status?: string | null): boolean {
	return (
		status === PipelineStatus.Failed || status === PipelineStatus.FailedStale || status === PipelineStatus.Canceled
	);
}

export interface ProcessingReadinessThresholds {
	delayedAt?: number;
	stalledAt?: number;
}

function getPathReadinessThresholds(
	runs: Array<PipelineStatusSchema | null | undefined>,
	uploadedAt: string | null | undefined,
	now: number,
): ProcessingReadinessThresholds {
	let hasCompletedRun = false;
	let earliestActiveStartedAt: number | undefined;
	let latestCompletedAt: number | undefined;

	for (const run of runs) {
		if (isRunCompleted(run)) {
			hasCompletedRun = true;
			const completedAt = Date.parse(run?.completed_at ?? '');
			if (Number.isFinite(completedAt) && completedAt <= now) {
				latestCompletedAt =
					latestCompletedAt === undefined ? completedAt : Math.max(latestCompletedAt, completedAt);
			}
		}

		if (isRunProcessing(run)) {
			const startedAt = Date.parse(run?.started_at ?? '');
			if (Number.isFinite(startedAt) && startedAt <= now) {
				earliestActiveStartedAt =
					earliestActiveStartedAt === undefined ? startedAt : Math.min(earliestActiveStartedAt, startedAt);
			}
		}
	}

	// uploaded_at is a usable episode clock only before any node has completed. Once processing has
	// history, an old upload timestamp may belong to a reprocessing episode and would mark it delayed
	// immediately. The API needs an episode timestamp before that case can be represented perfectly.
	const uploadTime = Date.parse(uploadedAt ?? '');
	let episodeStartedAt: number | undefined;
	if (!hasCompletedRun && Number.isFinite(uploadTime) && uploadTime <= now) {
		episodeStartedAt = uploadTime;
	} else if (earliestActiveStartedAt !== undefined) {
		// Prefer the current run clock for later-stage/reprocessing work.
		episodeStartedAt = earliestActiveStartedAt;
	} else if (latestCompletedAt !== undefined) {
		// During a handoff there may be no active run yet. Start from the latest completed node.
		episodeStartedAt = latestCompletedAt;
	}

	// A response can briefly carry a recoverable terminal status from an older processing episode
	// beside a newer active run. Only failures in the current episode should shorten its stall grace.
	let earliestRecoverableFailureAt: number | undefined;
	for (const run of runs) {
		if (!isRecoverableTerminalStatus(run?.status)) continue;
		const failedAt = Date.parse(run?.completed_at ?? run?.started_at ?? '');
		if (
			!Number.isFinite(failedAt) ||
			failedAt > now ||
			(episodeStartedAt !== undefined && failedAt < episodeStartedAt)
		) {
			continue;
		}
		earliestRecoverableFailureAt =
			earliestRecoverableFailureAt === undefined ? failedAt : Math.min(earliestRecoverableFailureAt, failedAt);
	}

	return {
		delayedAt: episodeStartedAt === undefined ? undefined : episodeStartedAt + DOCUMENT_PROCESSING_DELAY_MS,
		stalledAt:
			earliestRecoverableFailureAt !== undefined
				? earliestRecoverableFailureAt + RECOVERABLE_FAILURE_STALLED_MS
				: episodeStartedAt === undefined
					? undefined
					: episodeStartedAt + DOCUMENT_PROCESSING_STALLED_MS,
	};
}

export function getDocumentReadinessThresholds(
	doc: DocumentStatusV2Schema | null | undefined,
	uploadedAt: string | null | undefined,
	now: number,
): ProcessingReadinessThresholds {
	const runs = [doc?.bnd_v2, doc?.metadata_profiling, doc?.metadata_extraction, doc?.kbs, doc?.document_enrichment];
	const thresholds = getPathReadinessThresholds(runs, doc?.uploaded_at ?? uploadedAt, now);
	return isDocumentProcessingComplete(doc) || hasPermanentFailure(doc)
		? { delayedAt: thresholds.delayedAt }
		: thresholds;
}

export function getDocumentDelayedAt(
	doc: DocumentStatusV2Schema | null | undefined,
	uploadedAt: string | null | undefined,
	now: number,
): number | undefined {
	return getDocumentReadinessThresholds(doc, uploadedAt, now).delayedAt;
}

/**
 * Frontend-only escape hatch for processing episodes that never reach a hard terminal status.
 * Recoverable terminal statuses receive a shorter grace period; ordinary incomplete processing
 * receives a conservative six-hour ceiling based on the same episode clock as the delayed state.
 */
export function getDocumentStalledAt(
	doc: DocumentStatusV2Schema | null | undefined,
	uploadedAt: string | null | undefined,
	now: number,
): number | undefined {
	return getDocumentReadinessThresholds(doc, uploadedAt, now).stalledAt;
}

export function getKnowledgeBaseReadinessThresholds(
	doc: DocumentStatusV2Schema | null | undefined,
	uploadedAt: string | null | undefined,
	now: number,
): ProcessingReadinessThresholds {
	const thresholds = getPathReadinessThresholds(
		[doc?.bnd_v2, doc?.metadata_profiling, doc?.metadata_extraction, doc?.kbs],
		doc?.uploaded_at ?? uploadedAt,
		now,
	);
	return isRunCompleted(doc?.kbs) || hasKnowledgeBasePermanentFailure(doc)
		? { delayedAt: thresholds.delayedAt }
		: thresholds;
}

export function getKnowledgeBaseStalledAt(
	doc: DocumentStatusV2Schema | null | undefined,
	uploadedAt: string | null | undefined,
	now: number,
): number | undefined {
	return getKnowledgeBaseReadinessThresholds(doc, uploadedAt, now).stalledAt;
}

export function getKnowledgeBaseDelayedAt(
	doc: DocumentStatusV2Schema | null | undefined,
	uploadedAt: string | null | undefined,
	now: number,
): number | undefined {
	return getKnowledgeBaseReadinessThresholds(doc, uploadedAt, now).delayedAt;
}

// ---------------------------------------------------------------------------
// Document-level "in flight" predicates.
//
// These are defined by the TERMINAL node of a path, not by "some stage is in_progress". The pipeline
// advances one node at a time and a successor's run row is only created when its predecessor finishes,
// so between stages the successor field is `null` (reads as neither completed nor running). Counting
// in_progress therefore drops to zero in those handoff gaps and flickers the processing UI. Keying off
// the terminal node — "not done yet, and nothing failed" — is immune to that.
// ---------------------------------------------------------------------------

/**
 * KB-readiness path: bnd_v2 → metadata_profiling → metadata_extraction → kbs.
 * In flight until kbs COMPLETES, unless any stage on the path FAILED.
 */
export function isKbPathInFlight(doc?: DocumentStatusV2Schema | null): boolean {
	if (!doc) return false;
	if (isRunCompleted(doc.kbs)) return false;
	return !(
		isRunFailed(doc.bnd_v2) ||
		isRunFailed(doc.metadata_profiling) ||
		isRunFailed(doc.metadata_extraction) ||
		isRunFailed(doc.kbs)
	);
}

/**
 * Tag/summary path: bnd_v2 → metadata_profiling → document_enrichment (forks after profiling).
 * In flight until document_enrichment COMPLETES, unless a stage on the path FAILED.
 */
export function isEnrichmentPathInFlight(doc?: DocumentStatusV2Schema | null): boolean {
	if (!doc) return false;
	if (isRunCompleted(doc.document_enrichment)) return false;
	return !(isRunFailed(doc.bnd_v2) || isRunFailed(doc.metadata_profiling) || isRunFailed(doc.document_enrichment));
}
